<template>
  <v-dialog v-model="isOpen" max-width="500px" persistent>
    <v-card color="black-card" class="signup-modal">
      <v-card-title class="text-h5 pa-6 d-flex justify-space-between align-center">
        <span class="text-white">회원가입</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="closeModal"
          size="small"
        ></v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- 소셜 로그인 버튼들 -->
        <SocialLogin @success="handleSignupSuccess" />

        <v-divider class="my-6">
          <span class="px-4 text-caption text-gray-dark">또는</span>
        </v-divider>

        <!-- 이메일 회원가입 폼 -->
        <v-form @submit.prevent="handleEmailSignup" ref="formRef">
          <v-text-field
            v-model="displayName"
            label="이름"
            variant="outlined"
            prepend-inner-icon="mdi-account"
            color="primary"
            :rules="[rules.required]"
            required
            dark
          ></v-text-field>

          <v-text-field
            v-model="email"
            label="이메일"
            type="email"
            variant="outlined"
            prepend-inner-icon="mdi-email"
            color="primary"
            :rules="[rules.required, rules.email]"
            required
            dark
          ></v-text-field>

          <v-text-field
            v-model="password"
            label="비밀번호"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            prepend-inner-icon="mdi-lock"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            color="primary"
            :rules="[rules.required, rules.minLength]"
            required
            dark
            hint="최소 6자 이상"
          ></v-text-field>

          <v-text-field
            v-model="passwordConfirm"
            label="비밀번호 확인"
            :type="showPasswordConfirm ? 'text' : 'password'"
            variant="outlined"
            prepend-inner-icon="mdi-lock-check"
            :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
            color="primary"
            :rules="[rules.required, rules.passwordMatch]"
            required
            dark
          ></v-text-field>

          <v-alert v-if="error" type="error" class="mb-4" variant="tonal">
            {{ error }}
          </v-alert>

          <v-btn
            type="submit"
            color="primary"
            block
            size="large"
            :loading="loading"
            class="mb-4"
          >
            회원가입
          </v-btn>
        </v-form>

        <div class="text-center">
          <v-btn
            @click="switchToLogin"
            variant="text"
            color="primary"
            size="small"
          >
            이미 계정이 있으신가요? 로그인
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import SocialLogin from './SocialLogin.vue'

const props = defineProps({
  modelValue: Boolean
})

const emit = defineEmits(['update:modelValue', 'switch-to-login'])

const router = useRouter()

const displayName = ref('')
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const loading = ref(false)
const error = ref(null)
const formRef = ref(null)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 유효성 검증 규칙
const rules = {
  required: (value) => !!value || '필수 항목입니다.',
  email: (value) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(value) || '올바른 이메일 형식이 아닙니다.'
  },
  minLength: (value) => value.length >= 6 || '최소 6자 이상 입력해주세요.',
  passwordMatch: (value) => value === password.value || '비밀번호가 일치하지 않습니다.'
}

const handleEmailSignup = async () => {
  const { valid } = await formRef.value.validate()

  if (!valid) {
    return
  }

  loading.value = true
  error.value = null

  // 임시 회원가입 로직 (Firebase 연동 전)
  setTimeout(() => {
    console.log('회원가입 시도:', { displayName: displayName.value, email: email.value })
    handleSignupSuccess()
    loading.value = false
  }, 1000)
}

const handleSignupSuccess = () => {
  isOpen.value = false
  router.push('/classroom')
}

const closeModal = () => {
  isOpen.value = false
  displayName.value = ''
  email.value = ''
  password.value = ''
  passwordConfirm.value = ''
  error.value = null
}

const switchToLogin = () => {
  isOpen.value = false
  emit('switch-to-login')
}
</script>

<style scoped>
.signup-modal {
  border: 2px solid var(--mocha-mousse);
}

.v-card-title {
  border-bottom: 1px solid rgba(164, 120, 100, 0.2);
}
</style>
